/* eslint-disable */
import { useState, useMemo, useEffect } from "react";
import { saveToStorage, loadFromStorage } from "./Auth";
import { saveReportToCloud, loadReportsFromCloud, deleteReportFromCloud } from "./SupabaseSync";

/* ═══════════════════════════════════════════════════════════
   FOOTY ANALYST — SCOUT REPORTS SYSTEM
   File reports · Store locally · Filter & search
   Interest levels · Export · Full report viewer
═══════════════════════════════════════════════════════════ */

const SR_CSS = `
.sr-wrap { font-family: 'DM Sans', sans-serif; background: var(--cream); min-height: 100%; }

/* HERO */
.sr-hero { background: var(--teal2); border-bottom: 3px solid var(--mustard); padding: 1.8rem; position: relative; overflow: hidden; }
.sr-hero::before { content:''; position:absolute; inset:0; background:repeating-linear-gradient(0deg,transparent,transparent 30px,rgba(0,0,0,0.04) 30px,rgba(0,0,0,0.04) 31px); pointer-events:none; }
.sr-hero-inner { position:relative; z-index:1; display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:1rem; }
.sr-hero-title { font-family:'Bebas Neue',sans-serif; font-size:2rem; letter-spacing:3px; color:var(--white); line-height:1; }
.sr-hero-sub { font-size:0.75rem; color:rgba(255,255,255,0.55); font-style:italic; font-weight:300; margin-top:0.25rem; max-width:480px; line-height:1.6; }
.sr-hero-stats { display:flex; gap:1.5rem; }
.sr-stat { text-align:center; }
.sr-stat-n { font-family:'Bebas Neue',sans-serif; font-size:1.6rem; letter-spacing:2px; color:var(--mustard); line-height:1; }
.sr-stat-l { font-size:0.54rem; font-weight:700; letter-spacing:2px; text-transform:uppercase; color:rgba(255,255,255,0.45); margin-top:0.1rem; }

/* TABS */
.sr-tabs { display:flex; background:var(--white); border-bottom:2px solid var(--rule); }
.sr-tab { padding:0.85rem 1.4rem; font-family:'DM Sans',sans-serif; font-size:0.73rem; font-weight:800; letter-spacing:1.5px; text-transform:uppercase; cursor:pointer; color:var(--muted); border-bottom:3px solid transparent; margin-bottom:-2px; transition:all 0.2s; white-space:nowrap; }
.sr-tab:hover { color:var(--teal); }
.sr-tab.on { color:var(--teal); border-bottom-color:var(--teal); }

/* CONTENT */
.sr-content { padding:1.5rem 1.8rem; }

/* ── REPORT FORM ── */
.sr-form-wrap { display:grid; grid-template-columns:1fr 340px; gap:1.2rem; }
.sr-form-main { background:var(--white); border:1.5px solid var(--rule); padding:1.3rem; }
.sr-form-side { display:flex; flex-direction:column; gap:0.9rem; }
.sr-form-title { font-family:'Bebas Neue',sans-serif; font-size:1rem; letter-spacing:2.5px; color:var(--ink); margin-bottom:1rem; padding-bottom:0.65rem; border-bottom:1.5px solid var(--rule); }
.sr-field { margin-bottom:0.95rem; }
.sr-label { display:block; font-size:0.6rem; font-weight:800; letter-spacing:2px; text-transform:uppercase; color:var(--muted); margin-bottom:0.35rem; }
.sr-input { width:100%; background:var(--cream); border:1.5px solid var(--rule); border-bottom:2px solid var(--ink); padding:0.62rem 0.85rem; color:var(--ink); font-family:'DM Sans',sans-serif; font-size:0.85rem; font-weight:500; outline:none; }
.sr-input:focus { border-bottom-color:var(--teal); }
.sr-select { width:100%; background:var(--cream); border:1.5px solid var(--rule); border-bottom:2px solid var(--ink); padding:0.62rem 0.85rem; color:var(--ink); font-family:'DM Sans',sans-serif; font-size:0.85rem; font-weight:500; outline:none; cursor:pointer; }
.sr-textarea { width:100%; background:var(--cream); border:1.5px solid var(--rule); border-bottom:2px solid var(--ink); padding:0.62rem 0.85rem; color:var(--ink); font-family:'DM Sans',sans-serif; font-size:0.82rem; font-weight:400; outline:none; resize:vertical; min-height:90px; font-style:italic; }
.sr-textarea:focus { border-bottom-color:var(--teal); }
.sr-form-row { display:grid; grid-template-columns:1fr 1fr; gap:0.85rem; }
.sr-form-row-3 { display:grid; grid-template-columns:1fr 1fr 1fr; gap:0.85rem; }

/* Attribute sliders */
.sr-attrs-grid { display:grid; grid-template-columns:1fr 1fr; gap:0.6rem 1.2rem; margin:0.8rem 0; }
.sr-attr-row { display:flex; align-items:center; gap:0.6rem; }
.sr-attr-lbl { font-size:0.65rem; font-weight:700; color:var(--muted); width:72px; flex-shrink:0; text-transform:uppercase; letter-spacing:0.5px; }
.sr-attr-range { flex:1; accent-color:var(--teal); }
.sr-attr-val { font-family:'Bebas Neue',sans-serif; font-size:1rem; letter-spacing:1px; width:26px; text-align:right; }

/* Star rating */
.sr-stars { display:flex; gap:0.35rem; margin-top:0.2rem; }
.sr-star { font-size:1.4rem; cursor:pointer; color:var(--mustard); opacity:0.2; transition:opacity 0.15s; }
.sr-star.on { opacity:1; }

/* Interest buttons */
.sr-interest-grid { display:grid; grid-template-columns:1fr 1fr; gap:0.45rem; margin-top:0.3rem; }
.sr-int-btn { padding:0.55rem; text-align:center; font-family:'DM Sans',sans-serif; font-size:0.68rem; font-weight:800; letter-spacing:1px; text-transform:uppercase; cursor:pointer; border:1.5px solid var(--rule); color:var(--muted); background:transparent; transition:all 0.2s; }
.sr-int-btn.on.sign    { background:#16a34a; border-color:#16a34a; color:var(--white); }
.sr-int-btn.on.monitor { background:var(--mustard); border-color:var(--mustard); color:var(--ink); }
.sr-int-btn.on.trial   { background:var(--teal); border-color:var(--teal); color:var(--white); }
.sr-int-btn.on.pass    { background:var(--darkred); border-color:var(--darkred); color:var(--white); }

/* Side panel cards */
.sr-side-card { background:var(--white); border:1.5px solid var(--rule); padding:1rem; }
.sr-side-title { font-family:'Bebas Neue',sans-serif; font-size:0.88rem; letter-spacing:2px; color:var(--ink); margin-bottom:0.7rem; border-bottom:1.5px solid var(--rule); padding-bottom:0.5rem; }

/* Submit */
.sr-submit { width:100%; padding:0.95rem; background:var(--teal); color:var(--white); border:2px solid var(--teal); font-family:'DM Sans',sans-serif; font-weight:800; font-size:0.82rem; letter-spacing:2.5px; text-transform:uppercase; cursor:pointer; transition:all 0.2s; }
.sr-submit:hover { background:var(--teal2); border-color:var(--teal2); }
.sr-submit:disabled { opacity:0.5; cursor:not-allowed; }
.sr-saved-msg { background:var(--teal4); border:1.5px solid var(--teal); border-left:4px solid var(--teal); padding:0.75rem 1rem; margin-top:0.8rem; font-family:'DM Sans',sans-serif; font-size:0.78rem; font-weight:700; color:var(--teal2); }

/* ── REPORTS LIST ── */
.sr-controls { background:var(--cream2); border-bottom:1.5px solid var(--rule); padding:0.85rem 1.8rem; display:flex; gap:0.7rem; align-items:center; flex-wrap:wrap; }
.sr-search { flex:1; min-width:180px; background:var(--white); border:1.5px solid var(--rule); border-bottom:2.5px solid var(--teal); padding:0.55rem 0.85rem; font-family:'DM Sans',sans-serif; font-size:0.82rem; font-weight:500; color:var(--ink); outline:none; }
.sr-filter-btn { padding:0.45rem 0.85rem; font-family:'DM Sans',sans-serif; font-size:0.65rem; font-weight:800; letter-spacing:1.5px; text-transform:uppercase; cursor:pointer; border:1.5px solid var(--rule); color:var(--muted); background:var(--white); transition:all 0.2s; }
.sr-filter-btn:hover { border-color:var(--teal); color:var(--teal); }
.sr-filter-btn.on { background:var(--teal); border-color:var(--teal); color:var(--white); }
.sr-filter-btn.sign    { background:#16a34a; border-color:#16a34a; color:var(--white); }
.sr-filter-btn.monitor { background:var(--mustard); border-color:var(--mustard); color:var(--ink); }
.sr-filter-btn.pass    { background:var(--darkred); border-color:var(--darkred); color:var(--white); }

/* Report card */
.rpt-card { background:var(--white); border:1.5px solid var(--rule); margin-bottom:0.7rem; transition:all 0.2s; cursor:pointer; animation:rptFadeIn 0.3s ease both; }
@keyframes rptFadeIn { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
.rpt-card:hover { border-color:var(--ink); box-shadow:3px 3px 0 var(--ink); }
.rpt-head { display:flex; align-items:flex-start; gap:1rem; padding:1rem 1.1rem; border-bottom:1px solid var(--rule2); flex-wrap:wrap; }
.rpt-av { width:44px; height:44px; border-radius:50%; background:var(--teal); border:2px solid var(--ink); display:flex; align-items:center; justify-content:center; font-size:0.75rem; font-weight:800; color:var(--white); flex-shrink:0; }
.rpt-info { flex:1; }
.rpt-name { font-family:'DM Sans',sans-serif; font-size:1rem; font-weight:800; color:var(--ink); }
.rpt-meta { font-size:0.65rem; color:var(--muted); font-weight:400; margin-top:0.1rem; }
.rpt-tags { display:flex; gap:0.35rem; margin-top:0.4rem; flex-wrap:wrap; }
.rpt-tag { font-size:0.56rem; font-weight:800; padding:0.1rem 0.42rem; letter-spacing:1.5px; text-transform:uppercase; border:1.5px solid; }
.rpt-interest { font-size:0.62rem; font-weight:800; padding:0.2rem 0.65rem; letter-spacing:1.5px; text-transform:uppercase; align-self:flex-start; }
.rpt-interest.sign    { background:#16a34a; color:var(--white); }
.rpt-interest.monitor { background:var(--mustard); color:var(--ink); }
.rpt-interest.trial   { background:var(--teal); color:var(--white); }
.rpt-interest.pass    { background:var(--darkred); color:var(--white); }
.rpt-stars { color:var(--mustard); font-size:0.85rem; letter-spacing:2px; }
.rpt-body { padding:0.85rem 1.1rem; display:flex; gap:1.2rem; flex-wrap:wrap; align-items:flex-start; }
.rpt-notes { flex:1; min-width:200px; font-size:0.78rem; color:var(--ink2); font-style:italic; font-weight:300; line-height:1.7; }
.rpt-attrs-mini { display:grid; grid-template-columns:repeat(3,1fr); gap:2px; min-width:160px; }
.rpt-attr { background:var(--cream2); padding:0.22rem 0.35rem; display:flex; justify-content:space-between; align-items:center; border:1px solid var(--rule2); }
.rpt-attr-l { font-size:0.52rem; font-weight:700; color:var(--muted); text-transform:uppercase; letter-spacing:1px; }
.rpt-attr-v { font-family:'Bebas Neue',sans-serif; font-size:0.85rem; letter-spacing:1px; }
.rpt-foot { padding:0.6rem 1.1rem; border-top:1px solid var(--rule2); display:flex; align-items:center; justify-content:space-between; }
.rpt-foot-meta { font-size:0.62rem; color:var(--muted); font-style:italic; font-weight:300; }
.rpt-foot-actions { display:flex; gap:0.4rem; }
.rpt-action-btn { padding:0.3rem 0.75rem; font-family:'DM Sans',sans-serif; font-size:0.6rem; font-weight:800; letter-spacing:1.5px; text-transform:uppercase; cursor:pointer; border:1.5px solid var(--rule); color:var(--muted); background:transparent; transition:all 0.2s; }
.rpt-action-btn:hover { background:var(--teal); border-color:var(--teal); color:var(--white); }

/* Report detail modal */
.rpt-modal { position:fixed; inset:0; background:rgba(26,22,18,0.92); z-index:800; overflow-y:auto; padding:1.5rem; }
.rpt-modal-inner { max-width:760px; margin:0 auto; }
.rpt-modal-back { background:var(--teal); color:var(--white); border:2px solid var(--teal); padding:0.48rem 1.1rem; font-family:'DM Sans',sans-serif; font-size:0.72rem; font-weight:800; letter-spacing:2px; text-transform:uppercase; cursor:pointer; margin-bottom:1.2rem; transition:all 0.2s; }
.rpt-modal-back:hover { background:var(--ink); border-color:var(--ink); }

/* Empty */
.sr-empty { text-align:center; padding:4rem 2rem; }
.sr-empty-icon { font-size:3rem; margin-bottom:1rem; }
.sr-empty-title { font-family:'Bebas Neue',sans-serif; font-size:1.6rem; letter-spacing:2px; margin-bottom:0.4rem; }
.sr-empty-sub { font-size:0.85rem; color:var(--muted); font-style:italic; font-weight:300; max-width:380px; margin:0 auto; line-height:1.7; }
.sr-empty-btn { margin-top:1.5rem; padding:0.88rem 2.2rem; background:var(--teal); color:var(--white); border:2px solid var(--teal); font-family:'DM Sans',sans-serif; font-weight:800; font-size:0.8rem; letter-spacing:2.5px; text-transform:uppercase; cursor:pointer; transition:all 0.2s; }
.sr-empty-btn:hover { background:var(--teal2); }

@media(max-width:900px) {
  .sr-form-wrap { grid-template-columns:1fr; }
  .sr-form-row { grid-template-columns:1fr; }
  .sr-form-row-3 { grid-template-columns:1fr 1fr; }
}
`;

const POSITIONS = ["GK","CB","RB","LB","CDM","CM","CAM","LW","RW","ST"];
const INTEREST_LEVELS = [
  {id:"sign",label:"🟢 Sign Now",short:"Sign Now"},
  {id:"monitor",label:"🟡 Monitor",short:"Monitor"},
  {id:"trial",label:"🔵 Offer Trial",short:"Trial"},
  {id:"pass",label:"🔴 Pass",short:"Pass"},
];
const PLAYING_STYLES = [
  "Clinical Finisher","Target Man","False 9","Pressing Striker",
  "Inverted Winger","Wide Forward","Creative Winger","Pacey Winger",
  "Box-to-Box CM","Deep Playmaker","Press Trigger CM","Creative Playmaker","Advanced Playmaker",
  "Holding Pivot","Progressive Passer","Half-space Creator",
  "Ball-playing CB","Defensive CB","Aerial CB","Sweeper CB",
  "Attacking Full-back","Defensive Full-back","Inverted Full-back","Wing-back",
  "Sweeper Keeper","Shot-stopper","Distribution Keeper","Commanding Keeper",
];

// Seed reports for demo
const SEED_REPORTS = [
  {id:1,playerName:"Marcus Greenwood",age:"22",pos:"RW",club:"Riverside Athletic",nat:"🏴󠁧󠁢󠁥󠁮󠁧󠁿",style:"Inverted Winger",match:"vs North United",date:"2026-04-15",competition:"League",stars:4,interest:"sign",fee:"£280K",pace:88,shooting:79,passing:74,dribbling:87,defending:42,physical:70,notes:"Exceptional 1v1 — beat his man 7 times in the match. Left-footed on the right. Cut inside consistently and created 3 chances. Attitude was outstanding, pressed relentlessly. Injury history is worth checking — missed 6 weeks in January.",scout:"James Clarke",created:"15 Apr 2026"},
  {id:2,playerName:"Daniel Osei",age:"19",pos:"CDM",club:"City Academy",nat:"🇬🇭",style:"Holding Pivot",match:"vs East Rangers",date:"2026-03-28",competition:"Cup",stars:3,interest:"monitor",fee:"£180K",pace:68,shooting:55,passing:79,dribbling:72,defending:84,physical:82,notes:"Excellent positional sense for his age. Won 8 of 9 aerial duels. Reading of the game is ahead of his years — knew where to be before the ball arrived. Technical quality needs work — too many loose passes under press. Worth monitoring for another 3 months.",scout:"Mike Hunter",created:"28 Mar 2026"},
  {id:3,playerName:"Fabio Rossi",age:"24",pos:"ST",club:"Vale Town",nat:"🇮🇹",style:"Target Man",match:"vs South Athletic",date:"2026-03-12",competition:"League",stars:2,interest:"pass",fee:"",pace:67,shooting:80,passing:68,dribbling:58,defending:40,physical:90,notes:"Physically dominant — won every aerial duel. But touch was poor and link play was sloppy. Scored from a corner but offered little in open play. Not mobile enough for the system we play. Would be excellent in a more direct team.",scout:"James Clarke",created:"12 Mar 2026"},
  {id:4,playerName:"Kian Wallace",age:"17",pos:"LW",club:"County Borough",nat:"🏴󠁧󠁢󠁳󠁣󠁴󠁿",style:"Pacey Winger",match:"vs Athletic Wanderers",date:"2026-02-20",competition:"Youth",stars:5,interest:"sign",fee:"£95K",pace:94,shooting:72,passing:70,dribbling:91,defending:38,physical:65,notes:"The best 17-year-old I have seen this season. Extraordinary pace — just frightening. Beat his man 9 times in 65 minutes before they substituted him off. Left foot only currently but right foot improving. Needs developing but the ceiling is incredibly high. Must move quickly — he won't be available for long.",scout:"Mike Hunter",created:"20 Feb 2026"},
];

function ini(n){return n&&n.split(" ").map(w=>w[0]).join("")||"?";}
function attrC(v){return v>=80?"av-hi":v>=65?"av-md":"av-lo";}

const BLANK_REPORT = {
  playerName:"",age:"",pos:"ST",club:"",nat:"",style:"Clinical Finisher",
  match:"",date:"",competition:"",stars:0,interest:"",fee:"",
  pace:70,shooting:70,passing:70,dribbling:70,defending:50,physical:70,
  notes:"",strengthNotes:"",weaknessNotes:"",
};

/* ══════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════ */
export default function ScoutReports({isScout=false, initialReports=null}) {
  const [activeTab, setActiveTab] = useState("reports");
  const [reports, setReports] = useState(() => {
    if (initialReports && initialReports.length > 0) return initialReports;
    const saved = loadFromStorage("scout_reports", null);
    return saved || SEED_REPORTS;
  });
  const [form, setForm] = useState(BLANK_REPORT);
  const [saved, setSaved] = useState(false);
  const [search, setSearch] = useState("");
  const [interestFilter, setInterestFilter] = useState("All");
  const [posFilter, setPosFilter] = useState("All");
  const [selReport, setSelReport] = useState(null);
  const [sortBy, setSortBy] = useState("date");

  // Update if cloudData arrives after mount
  useEffect(() => {
    if (initialReports && initialReports.length > 0) {
      setReports(initialReports);
    }
  }, [initialReports?.length]);

  const saveReport = async () => {
    if (!form.playerName || !form.pos) return;
    const newReport = {
      ...form,
      id: Date.now(),
      scout: "Me",
      created: new Date().toLocaleDateString("en-GB", {day:"numeric",month:"short",year:"numeric"}),
    };
    const updated = [newReport, ...reports];
    setReports(updated);
    setSaved(true);
    await saveReportToCloud(newReport);
    setTimeout(() => { setSaved(false); setForm(BLANK_REPORT); setActiveTab("reports"); }, 2000);
  };

  const deleteReport = async (id, e) => {
    e.stopPropagation();
    const updated = reports.filter(r => r.id !== id);
    setReports(updated);
    await deleteReportFromCloud(id);
  };

  const filtered = useMemo(() => {
    let list = [...reports];
    if (search) list = list.filter(r =>
      r.playerName.toLowerCase().includes(search.toLowerCase()) ||
      r.club.toLowerCase().includes(search.toLowerCase()) ||
      r.pos.toLowerCase().includes(search.toLowerCase()) ||
      r.style.toLowerCase().includes(search.toLowerCase())
    );
    if (interestFilter !== "All") list = list.filter(r => r.interest === interestFilter);
    if (posFilter !== "All") list = list.filter(r => r.pos === posFilter);
    list.sort((a,b) => {
      if (sortBy === "stars") return b.stars - a.stars;
      if (sortBy === "name") return a.playerName.localeCompare(b.playerName);
      return b.id - a.id;
    });
    return list;
  }, [reports, search, interestFilter, posFilter, sortBy]);

  const signNow = reports.filter(r => r.interest === "sign").length;
  const monitoring = reports.filter(r => r.interest === "monitor").length;

  return (
    <>
      <style>{SR_CSS}</style>
      <div className="sr-wrap">

        {/* HERO */}
        <div className="sr-hero">
          <div className="sr-hero-inner">
            <div>
              <div className="sr-hero-title">Scout Reports</div>
              <div className="sr-hero-sub">File, store and filter your scouting reports. Every player you've watched, rated and recommended — all in one place.</div>
            </div>
            <div className="sr-hero-stats">
              <div className="sr-stat"><div className="sr-stat-n">{reports.length}</div><div className="sr-stat-l">Total Reports</div></div>
              <div className="sr-stat"><div className="sr-stat-n">{signNow}</div><div className="sr-stat-l">Sign Now</div></div>
              <div className="sr-stat"><div className="sr-stat-n">{monitoring}</div><div className="sr-stat-l">Monitoring</div></div>
            </div>
          </div>
        </div>

        {/* TABS */}
        <div className="sr-tabs">
          <div className={`sr-tab ${activeTab==="reports"?"on":""}`} onClick={()=>setActiveTab("reports")}>📋 All Reports ({reports.length})</div>
          <div className={`sr-tab ${activeTab==="file"?"on":""}`} onClick={()=>setActiveTab("file")}>✏️ File New Report</div>
          <div className={`sr-tab ${activeTab==="shortlist"?"on":""}`} onClick={()=>setActiveTab("shortlist")}>⭐ Shortlist ({signNow + monitoring})</div>
        </div>

        {/* ══ REPORTS LIST ══ */}
        {activeTab==="reports"&&(
          <>
            <div className="sr-controls">
              <input className="sr-search" placeholder="Search by name, club, position or style..." value={search} onChange={e=>setSearch(e.target.value)}/>
              <select style={{background:"var(--white)",border:"1.5px solid var(--rule)",padding:"0.5rem 0.75rem",fontFamily:"'DM Sans',sans-serif",fontSize:"0.75rem",fontWeight:600,color:"var(--ink)",outline:"none",cursor:"pointer"}} value={sortBy} onChange={e=>setSortBy(e.target.value)}>
                <option value="date">Sort: Latest</option>
                <option value="stars">Sort: Rating</option>
                <option value="name">Sort: Name</option>
              </select>
              {["All","sign","monitor","trial","pass"].map(f=>(
                <button key={f} className={`sr-filter-btn ${interestFilter===f?f==="All"?"on":f:""}`} onClick={()=>setInterestFilter(f)}>
                  {f==="All"?"All":f==="sign"?"🟢 Sign":f==="monitor"?"🟡 Monitor":f==="trial"?"🔵 Trial":"🔴 Pass"}
                </button>
              ))}
              <select style={{background:"var(--white)",border:"1.5px solid var(--rule)",padding:"0.5rem 0.75rem",fontFamily:"'DM Sans',sans-serif",fontSize:"0.75rem",fontWeight:600,color:"var(--ink)",outline:"none",cursor:"pointer"}} value={posFilter} onChange={e=>setPosFilter(e.target.value)}>
                <option>All</option>
                {POSITIONS.map(p=><option key={p}>{p}</option>)}
              </select>
            </div>

            <div className="sr-content">
              {filtered.length === 0 ? (
                <div className="sr-empty">
                  <div className="sr-empty-icon">📋</div>
                  <div className="sr-empty-title">{reports.length === 0 ? "No Reports Yet" : "No Matches Found"}</div>
                  <div className="sr-empty-sub">{reports.length === 0 ? "File your first scout report to start building your player database." : "Try adjusting your search or filters."}</div>
                  {reports.length === 0 && <button className="sr-empty-btn" onClick={()=>setActiveTab("file")}>File First Report →</button>}
                </div>
              ) : filtered.map((r,i) => (
                <div key={r.id} className="rpt-card" style={{animationDelay:`${i*0.04}s`}} onClick={()=>setSelReport(r)}>
                  <div className="rpt-head">
                    <div className="rpt-av" style={{background:r.pos==="GK"?"#f59e0b":["CB","RB","LB"].includes(r.pos)?"var(--darkred)":["CDM","CM","CAM"].includes(r.pos)?"var(--teal)":"#16a34a"}}>
                      {ini(r.playerName)}
                    </div>
                    <div className="rpt-info">
                      <div className="rpt-name">{r.playerName}</div>
                      <div className="rpt-meta">{r.nat} · {r.club} · Age {r.age} · {r.competition||"Match"}: {r.match}</div>
                      <div className="rpt-tags">
                        <span className="rpt-tag" style={{borderColor:"var(--teal)",color:"var(--teal)"}}>{r.pos}</span>
                        <span className="rpt-tag" style={{borderColor:"var(--muted)",color:"var(--muted)"}}>{r.style}</span>
                        {r.fee&&<span className="rpt-tag" style={{borderColor:"var(--mustard)",color:"var(--mustard2)"}}>Est. {r.fee}</span>}
                      </div>
                    </div>
                    <div style={{textAlign:"right",flexShrink:0}}>
                      <div className="rpt-stars">{"★".repeat(r.stars)}{"☆".repeat(5-r.stars)}</div>
                      {r.interest&&<div className={`rpt-interest ${r.interest}`} style={{marginTop:"0.4rem"}}>
                        {r.interest==="sign"?"🟢 Sign Now":r.interest==="monitor"?"🟡 Monitor":r.interest==="trial"?"🔵 Trial":"🔴 Pass"}
                      </div>}
                    </div>
                  </div>
                  <div className="rpt-body">
                    <div className="rpt-notes">{r.notes||"No notes filed."}</div>
                    <div className="rpt-attrs-mini">
                      {[["PAC",r.pace],["SHO",r.shooting],["PAS",r.passing],["DRI",r.dribbling],["DEF",r.defending],["PHY",r.physical]].map(([k,v])=>(
                        <div key={k} className="rpt-attr">
                          <span className="rpt-attr-l">{k}</span>
                          <span className={`rpt-attr-v ${attrC(v)}`}>{v}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="rpt-foot">
                    <div className="rpt-foot-meta">Filed by {r.scout||"Me"} · {r.created}</div>
                    <div className="rpt-foot-actions">
                      <button className="rpt-action-btn" onClick={e=>{e.stopPropagation();setSelReport(r);}}>View Full →</button>
                      <button className="rpt-action-btn" style={{borderColor:"var(--darkred)",color:"var(--darkred)"}} onClick={e=>deleteReport(r.id,e)}>Delete</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* ══ FILE REPORT ══ */}
        {activeTab==="file"&&(
          <div className="sr-content">
            <div className="sr-form-wrap">
              <div className="sr-form-main">
                <div className="sr-form-title">Player Information</div>
                <div className="sr-form-row">
                  <div className="sr-field">
                    <label className="sr-label">Player Name *</label>
                    <input className="sr-input" placeholder="Full name" value={form.playerName} onChange={e=>setForm(p=>({...p,playerName:e.target.value}))}/>
                  </div>
                  <div className="sr-field">
                    <label className="sr-label">Age</label>
                    <input className="sr-input" placeholder="e.g. 21" value={form.age} onChange={e=>setForm(p=>({...p,age:e.target.value}))}/>
                  </div>
                </div>
                <div className="sr-form-row">
                  <div className="sr-field">
                    <label className="sr-label">Club</label>
                    <input className="sr-input" placeholder="Current club" value={form.club} onChange={e=>setForm(p=>({...p,club:e.target.value}))}/>
                  </div>
                  <div className="sr-field">
                    <label className="sr-label">Nationality</label>
                    <input className="sr-input" placeholder="e.g. 🏴󠁧󠁢󠁥󠁮󠁧󠁿 English" value={form.nat} onChange={e=>setForm(p=>({...p,nat:e.target.value}))}/>
                  </div>
                </div>
                <div className="sr-form-row">
                  <div className="sr-field">
                    <label className="sr-label">Position *</label>
                    <select className="sr-select" value={form.pos} onChange={e=>setForm(p=>({...p,pos:e.target.value}))}>
                      {POSITIONS.map(p=><option key={p}>{p}</option>)}
                    </select>
                  </div>
                  <div className="sr-field">
                    <label className="sr-label">Playing Style</label>
                    <select className="sr-select" value={form.style} onChange={e=>setForm(p=>({...p,style:e.target.value}))}>
                      {PLAYING_STYLES.map(s=><option key={s}>{s}</option>)}
                    </select>
                  </div>
                </div>

                <div className="sr-form-title" style={{marginTop:"1.1rem"}}>Match Details</div>
                <div className="sr-form-row-3">
                  <div className="sr-field">
                    <label className="sr-label">Match</label>
                    <input className="sr-input" placeholder="e.g. vs North United" value={form.match} onChange={e=>setForm(p=>({...p,match:e.target.value}))}/>
                  </div>
                  <div className="sr-field">
                    <label className="sr-label">Date</label>
                    <input className="sr-input" type="date" value={form.date} onChange={e=>setForm(p=>({...p,date:e.target.value}))}/>
                  </div>
                  <div className="sr-field">
                    <label className="sr-label">Competition</label>
                    <input className="sr-input" placeholder="e.g. League" value={form.competition} onChange={e=>setForm(p=>({...p,competition:e.target.value}))}/>
                  </div>
                </div>

                <div className="sr-form-title" style={{marginTop:"1.1rem"}}>Attribute Assessment</div>
                <div className="sr-attrs-grid">
                  {[["Pace","pace"],["Shooting","shooting"],["Passing","passing"],["Dribbling","dribbling"],["Defending","defending"],["Physical","physical"]].map(([label,key])=>(
                    <div key={key} className="sr-attr-row">
                      <span className="sr-attr-lbl">{label}</span>
                      <input className="sr-attr-range" type="range" min="30" max="99" value={form[key]} onChange={e=>setForm(p=>({...p,[key]:Number(e.target.value)}))}/>
                      <span className={`sr-attr-val ${attrC(form[key])}`}>{form[key]}</span>
                    </div>
                  ))}
                </div>

                <div className="sr-form-title" style={{marginTop:"1.1rem"}}>Scout Notes</div>
                <div className="sr-field">
                  <label className="sr-label">Overall Observation</label>
                  <textarea className="sr-textarea" placeholder="Describe the player's performance — movement, attitude, technical quality, decision-making and anything that stood out..." value={form.notes} onChange={e=>setForm(p=>({...p,notes:e.target.value}))}/>
                </div>
                <div className="sr-form-row">
                  <div className="sr-field">
                    <label className="sr-label">Strengths</label>
                    <textarea className="sr-textarea" style={{minHeight:"65px"}} placeholder="What did they do well?" value={form.strengthNotes} onChange={e=>setForm(p=>({...p,strengthNotes:e.target.value}))}/>
                  </div>
                  <div className="sr-field">
                    <label className="sr-label">Areas to Develop</label>
                    <textarea className="sr-textarea" style={{minHeight:"65px"}} placeholder="Where do they need to improve?" value={form.weaknessNotes} onChange={e=>setForm(p=>({...p,weaknessNotes:e.target.value}))}/>
                  </div>
                </div>

                {saved && <div className="sr-saved-msg">✦ Report saved successfully! Redirecting to reports...</div>}
                <button className="sr-submit" onClick={saveReport} disabled={!form.playerName}>
                  {saved ? "✓ Saved!" : "Submit Scout Report →"}
                </button>
              </div>

              {/* Side panel */}
              <div className="sr-form-side">
                {/* Overall rating */}
                <div className="sr-side-card">
                  <div className="sr-side-title">Overall Rating</div>
                  <div className="sr-stars">
                    {[1,2,3,4,5].map(s=>(
                      <span key={s} className={`sr-star ${form.stars>=s?"on":""}`} onClick={()=>setForm(p=>({...p,stars:s}))}>★</span>
                    ))}
                  </div>
                  <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.65rem",color:"var(--muted)",fontStyle:"italic",marginTop:"0.5rem"}}>
                    {form.stars===0?"Not rated":form.stars===1?"Weak - not one for us":form.stars===2?"Below average":form.stars===3?"Decent option - worth monitoring":form.stars===4?"Good player - strong interest":form.stars===5?"Exceptional - sign immediately":""}
                  </div>
                </div>

                {/* Interest level */}
                <div className="sr-side-card">
                  <div className="sr-side-title">Interest Level</div>
                  <div className="sr-interest-grid">
                    {INTEREST_LEVELS.map(l=>(
                      <button key={l.id} className={`sr-int-btn ${form.interest===l.id?"on "+l.id:""}`} onClick={()=>setForm(p=>({...p,interest:l.id}))}>
                        {l.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Fee */}
                <div className="sr-side-card">
                  <div className="sr-side-title">Recommended Fee</div>
                  <input className="sr-input" placeholder="e.g. £250K or Free" value={form.fee} onChange={e=>setForm(p=>({...p,fee:e.target.value}))} style={{marginBottom:0}}/>
                  <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.62rem",color:"var(--muted)",fontStyle:"italic",marginTop:"0.45rem",lineHeight:1.5}}>Your estimated fair value — for internal reference only</div>
                </div>

                {/* Preview */}
                <div className="sr-side-card" style={{background:"var(--cream2)"}}>
                  <div className="sr-side-title">Attribute Preview</div>
                  <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:"2px"}}>
                    {[["PAC",form.pace],["SHO",form.shooting],["PAS",form.passing],["DRI",form.dribbling],["DEF",form.defending],["PHY",form.physical]].map(([k,v])=>(
                      <div key={k} className="rpt-attr">
                        <span className="rpt-attr-l">{k}</span>
                        <span className={`rpt-attr-v ${attrC(v)}`}>{v}</span>
                      </div>
                    ))}
                  </div>
                  <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.6rem",color:"var(--muted)",fontStyle:"italic",marginTop:"0.5rem"}}>Overall: {Math.round((form.pace+form.shooting+form.passing+form.dribbling+form.defending+form.physical)/6)}</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ══ SHORTLIST ══ */}
        {activeTab==="shortlist"&&(
          <div className="sr-content">
            {["sign","monitor"].map(level=>{
              const levelReports = reports.filter(r=>r.interest===level);
              if(levelReports.length===0) return null;
              return(
                <div key={level}>
                  <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"0.92rem",letterSpacing:"2px",color:level==="sign"?"#16a34a":"var(--mustard2)",marginBottom:"0.75rem",marginTop:level==="monitor"?"1.5rem":"0",display:"flex",alignItems:"center",gap:"0.5rem"}}>
                    <span>{level==="sign"?"🟢":"🟡"}</span>
                    <span>{level==="sign"?"Sign Now":"Monitoring"}</span>
                    <span style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.68rem",fontWeight:700,color:"var(--muted)",letterSpacing:"1px"}}>— {levelReports.length} player{levelReports.length!==1?"s":""}</span>
                  </div>
                  {levelReports.map((r,i)=>(
                    <div key={r.id} className="rpt-card" style={{animationDelay:`${i*0.04}s`}} onClick={()=>setSelReport(r)}>
                      <div className="rpt-head">
                        <div className="rpt-av" style={{background:r.pos==="GK"?"#f59e0b":["CB","RB","LB"].includes(r.pos)?"var(--darkred)":["CDM","CM","CAM"].includes(r.pos)?"var(--teal)":"#16a34a"}}>{ini(r.playerName)}</div>
                        <div className="rpt-info">
                          <div className="rpt-name">{r.playerName}</div>
                          <div className="rpt-meta">{r.nat} · {r.club} · Age {r.age} · {r.pos} · {r.style}</div>
                          {r.fee&&<div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.68rem",fontWeight:700,color:"var(--mustard2)",marginTop:"0.2rem"}}>Est. fee: {r.fee}</div>}
                        </div>
                        <div style={{textAlign:"right"}}>
                          <div className="rpt-stars">{"★".repeat(r.stars)}</div>
                          <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.62rem",color:"var(--muted)",fontStyle:"italic",marginTop:"0.3rem"}}>{r.created}</div>
                        </div>
                      </div>
                      <div className="rpt-body">
                        <div className="rpt-notes">{r.notes}</div>
                        <div className="rpt-attrs-mini">
                          {[["PAC",r.pace],["SHO",r.shooting],["PAS",r.passing],["DRI",r.dribbling],["DEF",r.defending],["PHY",r.physical]].map(([k,v])=>(
                            <div key={k} className="rpt-attr"><span className="rpt-attr-l">{k}</span><span className={`rpt-attr-v ${attrC(v)}`}>{v}</span></div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })}
            {reports.filter(r=>["sign","monitor"].includes(r.interest)).length===0&&(
              <div className="sr-empty">
                <div className="sr-empty-icon">⭐</div>
                <div className="sr-empty-title">Shortlist Empty</div>
                <div className="sr-empty-sub">File reports and mark players as "Sign Now" or "Monitor" to build your shortlist.</div>
                <button className="sr-empty-btn" onClick={()=>setActiveTab("file")}>File a Report →</button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── FULL REPORT MODAL ── */}
      {selReport&&(
        <div className="rpt-modal">
          <div className="rpt-modal-inner">
            <button className="rpt-modal-back" onClick={()=>setSelReport(null)}>← Back to Reports</button>

            {/* Header */}
            <div style={{background:"var(--teal2)",border:"1.5px solid var(--teal)",padding:"1.3rem",marginBottom:"1rem",display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:"1rem",flexWrap:"wrap"}}>
              <div>
                <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"2rem",letterSpacing:"3px",color:"var(--white)",lineHeight:1}}>{selReport.playerName}</div>
                <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.72rem",color:"rgba(255,255,255,0.55)",marginTop:"0.2rem"}}>{selReport.nat} · Age {selReport.age} · {selReport.club} · {selReport.pos} · {selReport.style}</div>
                <div style={{display:"flex",gap:"0.5rem",marginTop:"0.6rem",flexWrap:"wrap",alignItems:"center"}}>
                  <div style={{color:"var(--mustard)",fontSize:"1rem",letterSpacing:"2px"}}>{"★".repeat(selReport.stars)}{"☆".repeat(5-selReport.stars)}</div>
                  {selReport.interest&&<div className={`rpt-interest ${selReport.interest}`}>{selReport.interest==="sign"?"🟢 Sign Now":selReport.interest==="monitor"?"🟡 Monitor":selReport.interest==="trial"?"🔵 Trial":"🔴 Pass"}</div>}
                  {selReport.fee&&<div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.68rem",fontWeight:700,color:"var(--mustard)"}}>Est. {selReport.fee}</div>}
                </div>
              </div>
              <div style={{textAlign:"right"}}>
                <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"3rem",letterSpacing:"2px",color:"var(--mustard)",lineHeight:1}}>{Math.round((selReport.pace+selReport.shooting+selReport.passing+selReport.dribbling+selReport.defending+selReport.physical)/6)}</div>
                <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.52rem",color:"rgba(255,255,255,0.4)",letterSpacing:"2px",textTransform:"uppercase"}}>Overall</div>
              </div>
            </div>

            {/* Match context */}
            <div style={{background:"var(--white)",border:"1.5px solid var(--rule)",padding:"1rem",marginBottom:"1rem"}}>
              <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.6rem",fontWeight:800,letterSpacing:"2px",textTransform:"uppercase",color:"var(--teal)",marginBottom:"0.5rem"}}>Match Watched</div>
              <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.88rem",fontWeight:700,color:"var(--ink)"}}>{selReport.match} · {selReport.competition}</div>
              <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.68rem",color:"var(--muted)",marginTop:"0.1rem"}}>Scouted on {selReport.date} · Filed by {selReport.scout||"Me"} on {selReport.created}</div>
            </div>

            {/* Attributes */}
            <div style={{background:"var(--white)",border:"1.5px solid var(--rule)",padding:"1rem",marginBottom:"1rem"}}>
              <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.6rem",fontWeight:800,letterSpacing:"2px",textTransform:"uppercase",color:"var(--teal)",marginBottom:"0.7rem"}}>Attribute Assessment</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0.5rem"}}>
                {[["Pace",selReport.pace],["Shooting",selReport.shooting],["Passing",selReport.passing],["Dribbling",selReport.dribbling],["Defending",selReport.defending],["Physical",selReport.physical]].map(([k,v])=>(
                  <div key={k} style={{display:"flex",alignItems:"center",gap:"0.6rem"}}>
                    <span style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.65rem",fontWeight:700,color:"var(--muted)",textTransform:"uppercase",letterSpacing:"0.5px",width:"68px",flexShrink:0}}>{k}</span>
                    <div style={{flex:1,height:"6px",background:"var(--cream2)",borderRadius:"2px",position:"relative"}}>
                      <div style={{position:"absolute",top:0,left:0,height:"100%",width:`${v}%`,background:v>=80?"#16a34a":v>=65?"var(--mustard)":"var(--darkred)",borderRadius:"2px",opacity:0.8}}/>
                    </div>
                    <span style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"0.95rem",letterSpacing:"1px",color:v>=80?"#16a34a":v>=65?"var(--mustard2)":"var(--darkred)",width:"24px",textAlign:"right"}}>{v}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Notes */}
            {selReport.notes&&(
              <div style={{background:"var(--white)",border:"1.5px solid var(--rule)",borderLeft:"4px solid var(--teal)",padding:"1rem",marginBottom:"1rem"}}>
                <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.6rem",fontWeight:800,letterSpacing:"2px",textTransform:"uppercase",color:"var(--teal)",marginBottom:"0.5rem"}}>Scout Notes</div>
                <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.82rem",fontWeight:300,color:"var(--ink2)",lineHeight:1.75,fontStyle:"italic"}}>{selReport.notes}</div>
              </div>
            )}

            {/* Strengths / Weaknesses */}
            {(selReport.strengthNotes||selReport.weaknessNotes)&&(
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"1rem",marginBottom:"1rem"}}>
                {selReport.strengthNotes&&(
                  <div style={{background:"var(--white)",border:"1.5px solid var(--rule)",borderLeft:"4px solid #16a34a",padding:"1rem"}}>
                    <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.6rem",fontWeight:800,letterSpacing:"2px",textTransform:"uppercase",color:"#16a34a",marginBottom:"0.4rem"}}>Strengths</div>
                    <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.78rem",fontWeight:300,color:"var(--ink2)",lineHeight:1.65,fontStyle:"italic"}}>{selReport.strengthNotes}</div>
                  </div>
                )}
                {selReport.weaknessNotes&&(
                  <div style={{background:"var(--white)",border:"1.5px solid var(--rule)",borderLeft:"4px solid var(--darkred)",padding:"1rem"}}>
                    <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.6rem",fontWeight:800,letterSpacing:"2px",textTransform:"uppercase",color:"var(--darkred)",marginBottom:"0.4rem"}}>Areas to Develop</div>
                    <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.78rem",fontWeight:300,color:"var(--ink2)",lineHeight:1.65,fontStyle:"italic"}}>{selReport.weaknessNotes}</div>
                  </div>
                )}
              </div>
            )}

            <button style={{width:"100%",padding:"0.85rem",background:"var(--teal)",color:"var(--white)",border:"2px solid var(--teal)",fontFamily:"'DM Sans',sans-serif",fontWeight:800,fontSize:"0.78rem",letterSpacing:"2px",textTransform:"uppercase",cursor:"pointer"}} onClick={()=>setSelReport(null)}>
              Close Report
            </button>
          </div>
        </div>
      )}
    </>
  );
}
